import { useEffect, useState, type FormEvent } from 'react';

import type { SurveyDefinition } from '../domain-types';

export function SurveyPanel({ token }: { token: string }) {
  const config = window.QUALITY_VET_CONFIG;
  const [survey, setSurvey] = useState<SurveyDefinition | null>(null);
  const [answers, setAnswers] = useState<string[]>([]);
  const [message, setMessage] = useState('Loading survey…');
  const [submitting, setSubmitting] = useState(false);
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const loadSurvey = async () => {
      const response = await fetch(`${config.apiUrl}/v1/surveys/${token}`);
      if (!response.ok) {
        setMessage('This survey is unavailable, closed, or has expired.');
        return;
      }
      const definition = (await response.json()) as SurveyDefinition;
      setSurvey(definition);
      setAnswers(definition.questions.map(() => ''));
      setConsented(false);
      setMessage('');
    };
    void loadSurvey();
  }, [config.apiUrl, token]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!survey) return;
    setSubmitting(true);
    const response = await fetch(`${config.apiUrl}/v1/surveys/${token}/responses`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ answers, consent: consented }),
    });
    setSubmitting(false);
    if (!response.ok) {
      setMessage('Your response could not be submitted. The survey may now be closed.');
      return;
    }
    setSurvey(null);
    setMessage('Thank you. Your response has been recorded.');
  };

  return (
    <main className="survey-page">
      <a className="skip-link" href="#survey-content">
        Skip to survey
      </a>
      <a className="brand" href="https://quality.vet">
        quality<span>.vet</span>
      </a>
      <section id="survey-content" tabIndex={-1}>
        {survey ? (
          <>
            <p className="eyebrow">Stakeholder survey</p>
            <h1>{survey.title}</h1>
            {survey.description ? <p className="survey-intro">{survey.description}</p> : null}
            <form onSubmit={(event) => void submit(event)}>
              {survey.questions.map((question, index) => (
                <label key={`${index}-${question}`}>
                  {question}
                  <textarea
                    required
                    maxLength={4000}
                    rows={4}
                    value={answers[index] ?? ''}
                    onChange={(event) =>
                      setAnswers((current) =>
                        current.map((answer, answerIndex) =>
                          answerIndex === index ? event.target.value : answer,
                        ),
                      )
                    }
                  />
                </label>
              ))}
              <label className="survey-consent">
                <input
                  required
                  type="checkbox"
                  checked={consented}
                  onChange={(event) => setConsented(event.target.checked)}
                />
                <span>{survey.consentStatement}</span>
              </label>
              <button disabled={submitting || !consented} type="submit">
                {submitting ? 'Submitting…' : 'Submit response'}
              </button>
            </form>
            <small>Open until {new Date(survey.expiresAt).toLocaleString()}</small>
          </>
        ) : (
          <p aria-atomic="true" className="survey-message" role="status" aria-live="polite">
            {message}
          </p>
        )}
      </section>
    </main>
  );
}
